<script setup>
import { ref } from 'vue'
/** RadioButtonGroup */
/* Use for groups of facets in search/browse UIs:
    - in which all facets appear in a list
    - where the user can select one facet at a time
    - example: "Categories" in Browse Goals or Browse Present Levels.
*/

const props = defineProps({
  // The group of radio buttons: {label: String, value: String, disabled: Boolean}
  filterGroup: Array,
  // Label for the whole group.
  groupLabel: String,
  // Use "true" if filterGroup does not have any items.
  emptyState: Boolean,
  // A message for when filterGroup does not have any items. For example, "Select a subject."
  emptyStateMsg: String,
  // The selected button will be highlighted in this color.
  highlightColor: String,
  // The selected value. v-model in the parent.
  modelValue: String
})

const emit = defineEmits(['update:modelValue'])
const focusedIndex = ref(null)
const getClass = (idx, facetValue) => {
  let classes = []
  if (facetValue === props.modelValue) {
    classes.push(`selected-facet ${props.highlightColor} text-white`)
  }
  if (idx === focusedIndex.value) {
    classes.push('outline outline-2 outline-offset-1 outline-primary-400')
  }
  return classes.join(' ')
}
</script>

<template>
  <fieldset>
    <legend id="facet-label" class="text-gray-900 font-bold uppercase">{{ groupLabel }}</legend>
    <p class="text-sm italic" v-if="emptyState">{{ emptyStateMsg }}</p>
    <ul v-if="filterGroup && filterGroup.length" aria-labelledby="facet-label">
      <template v-for="(facet, i) in filterGroup">
        <li
          v-if="!emptyState"
          :id="`${groupLabel}-${i}`"
          :key="facet.value"
          class="facet-list-item my-2 h-8 rounded-xl px-4 pt-1 text-sm"
          :class="getClass(i, facet.value, highlightColor)"
        >
          <input
            type="radio"
            :disabled="facet.disabled"
            :name="groupLabel"
            :id="`facet-${facet.value}`"
            class="opacity-0"
            @input="emit('update:modelValue', facet.value)"
            @focus="focusedIndex = i"
            @blur="focusedIndex = null"
          />
          <label class="cursor-pointer align-middle" :for="`facet-${facet.value}`"
            >{{ facet.label }}
          </label>
        </li>
      </template>
    </ul>
  </fieldset>
</template>

<style scoped lang="postcss">
.facet-list-item:hover:not(.selected-facet) {
  background-color: #eee;
}
</style>
